import { cloneDeep, type DebouncedFunc, throttle } from 'lodash-es'
import React, { useMemo } from 'react'

import getBrowserStorage from '@/client/services/browserStorage/getBrowserStorage'

type Response<State> = {
  previousStateRestore: () => State
  previousStateAvailable: boolean
  previousStateClear: () => void
  saveState: DebouncedFunc<<T extends keyof State>(key: T, value: State[T]) => void>
  originalStateReset: () => State
}

const DEBOUNCE_MS_SAVE = 1000

export function usePersistedState<State>(identifier: string, initialState: State): Response<State> {
  // JavaScript objects are passed by reference, so we deep copy the initial state
  const currentState = React.useRef(cloneDeep(initialState))
  const [originalState, setOriginalState] = React.useState<State>(cloneDeep(initialState))

  const [previousState, setPreviousState] = React.useState<State | null>(null)
  const browserStorageRef = useMemo(() => getBrowserStorage<State>(identifier), [identifier])

  const previousStateClear = React.useCallback(() => {
    setPreviousState(null)

    function clearState() {
      browserStorageRef.del().catch(() => {
        // No Op
      })
    }
    // Schedule another deletion in case there's a debounced save in progress
    setTimeout(clearState, DEBOUNCE_MS_SAVE)
    // Clear immediately -- in case user closes tab or such
    clearState()
  }, [browserStorageRef])
  const previousStateRestore = React.useCallback(() => {
    if (previousState != null) {
      currentState.current = cloneDeep(previousState)
    }

    // Override the original state with the previous state
    setOriginalState(previousState ?? initialState)

    setPreviousState(null)
    return previousState ?? initialState
  }, [previousState, initialState])

  const saveState = useMemo(
    () =>
      throttle(
        <T extends keyof State>(key: T, value: State[T]): void => {
          currentState.current[key] = value
          browserStorageRef.set(currentState.current).catch(() => {
            // If it fails, it fails.
          })
        },
        DEBOUNCE_MS_SAVE,
        { trailing: true }
      ),
    [browserStorageRef]
  )

  const originalStateReset = React.useCallback(() => {
    currentState.current = cloneDeep(originalState)
    browserStorageRef.set(currentState.current).catch(() => {
      // If it fails, it fails.
    })
    return originalState
  }, [browserStorageRef, originalState])

  React.useEffect(() => {
    browserStorageRef
      .get()
      .then(result => {
        setPreviousState(result ?? null)
      })
      .catch(() => {
        // No op
      })
  }, [browserStorageRef])

  return {
    previousStateRestore,
    previousStateAvailable: previousState != null,
    previousStateClear,
    saveState,
    originalStateReset,
  }
}
